/**
 * Migration Studio — first-visit onboarding tour.
 *
 * A lightweight spotlight overlay that walks a new admin through the
 * A.1 → B.6 flow WITHOUT needing to read docs.
 *
 * - Auto-triggers on first visit (localStorage flag `migration_studio_tour_v1_seen`)
 * - 8 steps: Projects → Sources → Discovery/Mapping → Gaps/Coverage →
 *            Costing/Quote → Test Migration → UAT/Release → Rollback/Hypercare
 * - Skip / Prev / Next controls
 * - Replay via the `?` button in the studio header (fires event `migration-studio-tour-replay`)
 *
 * Design goals:
 * - Zero external deps
 * - Anchors each step to a sidebar rail button via `data-testid`
 * - Draws a bright spotlight + tooltip callout next to the target
 * - Falls back to a centered modal if the anchor is missing (e.g. on mobile)
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { X, ArrowRight, ArrowLeft, Sparkles, Volume2, VolumeX } from 'lucide-react';

const STORAGE_KEY = 'migration_studio_tour_v1_seen';
const EVENT_REPLAY = 'migration-studio-tour-replay';
const AUDIO_MUTE_KEY = 'migration_studio_tour_muted';
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Each step points to a sidebar rail button (`data-testid="rail-<section>"`).
// audioId maps to the backend TTS-generated mp3 (see routes/tour.py
// MIGRATION_STUDIO_TOUR_STEPS). Audio is generated ONCE server-side and
// served as a static file so subscribers can replay unlimited without credits.
const STEPS = [
  {
    id: 'projects',
    audioId: 'mig_studio_projects',
    label: 'Migration Projects',
    phase: 'A.1',
    anchorTestId: 'rail-projects',
    title: 'Start with a Migration Project',
    body: (
      <>
        Every migration begins as a <b>Project</b>. A project holds your source files, mappings,
        quotes, UAT sign-offs and release manifests. Use the <b>New Project Wizard</b> to spin one
        up in under a minute.
      </>
    ),
  },
  {
    id: 'sources',
    audioId: 'mig_studio_sources',
    label: 'Data Sources',
    phase: 'A.2',
    anchorTestId: 'rail-sources',
    title: 'Upload legacy files, safely',
    body: (
      <>
        Drop <b>CSV, XLSX or JSON</b> exports here. Every upload is scanned, signature-validated
        and quarantined in a tenant-isolated folder before the platform even reads a byte.
      </>
    ),
  },
  {
    id: 'discovery',
    audioId: 'mig_studio_mapping',
    label: 'Discovery & Mapping',
    phase: 'A.3 → A.4',
    anchorTestId: 'rail-mapping',
    title: 'Automatic column profiling + canonical mapping',
    body: (
      <>
        The <b>Profiling engine</b> infers types + PKs. The <b>Mapping Workbench</b> deterministically
        proposes canonical targets (invoice, contact, product…) with a confidence score. High-risk
        fields (financial totals, tax IDs) always require your explicit approval.
      </>
    ),
  },
  {
    id: 'gaps',
    audioId: 'mig_studio_gaps',
    label: 'Gaps & Coverage',
    phase: 'A.5',
    anchorTestId: 'rail-gaps',
    title: 'Close the gaps before you commit',
    body: (
      <>
        The <b>Gaps &amp; Proposals</b> pane surfaces unmapped columns, data-quality issues and
        tenant-custom metadata proposals. Coverage Report shows the exact % of source columns that
        will land in canonical objects.
      </>
    ),
  },
  {
    id: 'costing',
    audioId: 'mig_studio_costing',
    label: 'Costing & Quote',
    phase: 'B.1',
    anchorTestId: 'rail-costing',
    title: 'Generate a transparent quote',
    body: (
      <>
        A formula-based quote combines row count × complexity × payment mode. Operators can override
        the number with an audit-logged reason. Country operators + first-10 clients may accept with
        <b> pay-offline-deferred</b> billing.
      </>
    ),
  },
  {
    id: 'test',
    audioId: 'mig_studio_test',
    label: 'Test Migration',
    phase: 'B.3',
    anchorTestId: 'rail-test',
    title: 'Dry-run into a shadow collection',
    body: (
      <>
        The test-migration engine materialises canonical rows into an isolated
        <span className="font-mono"> migration_shadow_*</span> collection — <b>zero</b> production
        writes. Preview 20 rows before you approve.
      </>
    ),
  },
  {
    id: 'release',
    audioId: 'mig_studio_release',
    label: 'UAT & Release',
    phase: 'B.5',
    anchorTestId: 'rail-release',
    title: '3-role sign-off + release manifest',
    body: (
      <>
        Owner, Finance Reviewer and Data Steward all sign off. Release then builds a
        <b> forward + reverse plan</b> BEFORE any write. Production writes require an explicit
        opt-in on your Trust Center — no accidental clobbering.
      </>
    ),
  },
  {
    id: 'hypercare',
    audioId: 'mig_studio_hypercare',
    label: 'Rollback & Hypercare',
    phase: 'B.6',
    anchorTestId: 'rail-hypercare',
    title: 'Reversible with one click',
    body: (
      <>
        Every executed release stores its reverse plan. One <b>Rollback</b> click walks that plan
        and removes exactly what it wrote. Hypercare check-ins for 7 days post-release; the project
        auto-escalates to <b>AT_RISK</b> if a critical check comes in.
      </>
    ),
  },
];


export function useStudioTour() {
  const [visible, setVisible] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // Auto-trigger on first visit
  useEffect(() => {
    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (!seen) {
        // Small delay so the page renders before we highlight
        const t = setTimeout(() => setVisible(true), 700);
        return () => clearTimeout(t);
      }
    } catch { /* ignore */ }
  }, []);

  // Listen for replay events (fired by the "?" button in the header)
  useEffect(() => {
    const handler = () => { setStepIndex(0); setVisible(true); };
    window.addEventListener(EVENT_REPLAY, handler);
    return () => window.removeEventListener(EVENT_REPLAY, handler);
  }, []);

  const close = () => {
    setVisible(false);
    try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch { /* ignore */ }
  };

  return { visible, stepIndex, setStepIndex, close };
}


export function StudioTour({ onFocusSection }) {
  const { visible, stepIndex, setStepIndex, close } = useStudioTour();
  const [anchorRect, setAnchorRect] = useState(null);
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(AUDIO_MUTE_KEY) === '1'; } catch { return false; }
  });
  const [audioReady, setAudioReady] = useState(false);
  const cardRef = useRef(null);
  const audioRef = useRef(null);

  const step = STEPS[stepIndex];

  // Persist mute pref
  useEffect(() => {
    try { localStorage.setItem(AUDIO_MUTE_KEY, muted ? '1' : '0'); } catch { /* noop */ }
  }, [muted]);

  // Audio autoplay + auto-next on ended (matches AutoMiniTour behaviour).
  useEffect(() => {
    if (!visible || !step) return;
    // Stop any previous playback
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    setAudioReady(false);
    if (muted) return;
    const url = `${API_BASE}/api/tour/audio/${step.audioId}`;
    const a = new Audio(url);
    a.preload = 'auto';
    audioRef.current = a;
    const onEnded = () => {
      if (stepIndex < STEPS.length - 1) {
        setStepIndex(stepIndex + 1);
      } else {
        // Last step: hold on-screen but stop auto-advance
      }
    };
    a.addEventListener('ended', onEnded);
    a.addEventListener('canplay', () => setAudioReady(true));
    a.addEventListener('error', () => setAudioReady(false));
    a.play().catch(() => {
      // Autoplay blocked — user can tap the speaker icon to start
    });
    return () => {
      a.pause();
      a.removeEventListener('ended', onEnded);
      audioRef.current = null;
    };
  }, [visible, step, stepIndex, muted, setStepIndex]);

  // Re-measure the anchor whenever the step changes or window resizes.
  useLayoutEffect(() => {
    if (!visible || !step) return;
    onFocusSection?.(step.id);
    const measure = () => {
      const el = document.querySelector(`[data-testid="${step.anchorTestId}"]`);
      if (el) {
        try { el.scrollIntoView({ block: 'center', behavior: 'auto' }); } catch { /* ignore */ }
        const r = el.getBoundingClientRect();
        setAnchorRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else {
        setAnchorRect(null);
      }
    };
    const raf = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [visible, step, stepIndex, onFocusSection]);

  if (!visible || !step) return null;

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === STEPS.length - 1;
  const totalSteps = STEPS.length;

  // Position the caption card. NEW: always keep the card fully in-viewport with
  // 24px margins + a max-height so long bodies scroll rather than clip.
  const CARD_W = 380;
  const MAX_H = Math.min(560, window.innerHeight - 48);
  let cardStyle = { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
  if (anchorRect) {
    const pad = 24;
    const wouldFitRight = anchorRect.left + anchorRect.width + pad + CARD_W < window.innerWidth;
    // Anchor the card to the anchor's vertical center but clamp so it never
    // goes off-screen. `top` becomes the anchor-center minus half the max-height,
    // clamped to [24, viewport - max_h - 24].
    const desiredTop = anchorRect.top + anchorRect.height / 2 - MAX_H / 2;
    const clampedTop = Math.max(24, Math.min(desiredTop, window.innerHeight - MAX_H - 24));
    if (wouldFitRight) {
      cardStyle = { left: anchorRect.left + anchorRect.width + pad, top: clampedTop, transform: 'none' };
    } else {
      cardStyle = { left: Math.max(24, anchorRect.left), top: clampedTop, transform: 'none' };
    }
  }

  return (
    <div className="fixed inset-0 z-[70] pointer-events-none" data-testid="studio-tour-overlay">
      {/* Dim + spotlight — pointer-events-none on the rectangles so the sidebar rail
          stays clickable through the tour. Click-outside-to-dismiss lives on the card's
          close button only. */}
      {anchorRect ? (
        <>
          {/* 4 dim rectangles around the anchor (leaves the anchor clear) */}
          <div className="absolute inset-x-0 top-0 bg-black/60 pointer-events-none"
                style={{ height: Math.max(0, anchorRect.top - 4) }} />
          <div className="absolute inset-x-0 bg-black/60 pointer-events-none"
                style={{ top: anchorRect.top + anchorRect.height + 4, bottom: 0 }} />
          <div className="absolute bg-black/60 pointer-events-none"
                style={{ top: Math.max(0, anchorRect.top - 4),
                         height: anchorRect.height + 8, left: 0,
                         width: Math.max(0, anchorRect.left - 4) }} />
          <div className="absolute bg-black/60 pointer-events-none"
                style={{ top: Math.max(0, anchorRect.top - 4),
                         height: anchorRect.height + 8,
                         left: anchorRect.left + anchorRect.width + 4, right: 0 }} />
          {/* Spotlight ring around anchor */}
          <div className="absolute rounded-md ring-2 ring-emerald-400 ring-offset-2 ring-offset-black/60 pointer-events-none"
                style={{ top: anchorRect.top - 4, left: anchorRect.left - 4,
                         width: anchorRect.width + 8, height: anchorRect.height + 8 }} />
        </>
      ) : (
        <div className="absolute inset-0 bg-black/60 pointer-events-auto" onClick={close} />
      )}

      {/* Caption card — flex column with scrollable body so long text never clips. */}
      <div
        ref={cardRef}
        className="absolute pointer-events-auto bg-white rounded-lg shadow-2xl border border-slate-200 flex flex-col"
        style={{ width: CARD_W, maxHeight: MAX_H, ...cardStyle }}
        data-testid="studio-tour-card"
      >
        <div className="p-5 pb-3 flex items-start justify-between gap-2 border-b border-slate-100">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Sparkles className="w-4 h-4 text-emerald-500" />
              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px]">{step.phase}</Badge>
              <span className="text-[11px] text-slate-500 font-medium" data-testid="studio-tour-step-counter">
                Step {stepIndex + 1} of {totalSteps} · {step.label}
              </span>
            </div>
            <div className="font-semibold text-slate-900 text-base leading-snug" data-testid="studio-tour-title">{step.title}</div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => setMuted((m) => !m)}
              className="text-slate-400 hover:text-emerald-700 p-1 rounded transition-colors"
              data-testid="studio-tour-audio-toggle"
              aria-label={muted ? 'Unmute narration' : 'Mute narration'}
              title={muted ? 'Enable narration' : 'Mute narration'}
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className={`w-4 h-4 ${audioReady ? 'text-emerald-600' : ''}`} />}
            </button>
            <button
              onClick={close}
              className="text-slate-400 hover:text-slate-700 p-1 rounded"
              data-testid="studio-tour-close-btn"
              aria-label="Close tour"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          <p className="text-sm text-slate-600 leading-relaxed" data-testid="studio-tour-body">
            {step.body}
          </p>
        </div>

        <div className="px-5 pb-4 pt-2 border-t border-slate-100 flex-shrink-0">
          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1.5 mb-3">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === stepIndex ? 'w-6 bg-emerald-500' : 'w-1.5 bg-slate-200'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={close}
              className="text-slate-500 hover:text-slate-700"
              data-testid="studio-tour-skip-btn"
            >
              Skip tour
            </Button>
            <div className="flex items-center gap-2">
              {!isFirst && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setStepIndex(stepIndex - 1)}
                  data-testid="studio-tour-prev-btn"
                >
                  <ArrowLeft className="w-3.5 h-3.5 mr-1" />Back
                </Button>
              )}
              {isLast ? (
                <Button
                  size="sm"
                  onClick={close}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="studio-tour-finish-btn"
                >
                  Finish
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => setStepIndex(stepIndex + 1)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="studio-tour-next-btn"
                >
                  Next<ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


export function StudioTourReplayButton() {
  return (
    <button
      onClick={() => window.dispatchEvent(new CustomEvent(EVENT_REPLAY))}
      className="text-[11px] text-slate-500 hover:text-emerald-700 flex items-center gap-1 border border-slate-200 rounded-full px-2.5 py-1 hover:border-emerald-300 transition-colors"
      data-testid="studio-tour-replay-btn"
      title="Replay onboarding tour"
    >
      <Sparkles className="w-3 h-3" />
      Tour
    </button>
  );
}
